class SearchDrawer extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="text"]');
    this.reset = this.querySelector('.search_drawer_reset');
    this.emptyWrapper = this.querySelector('.empty_search_drawer');
    this.resultWrapper = this.querySelector('.result_search_drawer');
    this.resultInner = this.resultWrapper.querySelector('.result_search_inner_popup');
  }

  connectedCallback() {
    if (this.input) {
      this.input.addEventListener(
        'input',
        this.debounce(this.handleInput.bind(this), 300)
      );
    }

    this.reset?.addEventListener(
      'click',
      this.handleReset.bind(this)
    );

    this.input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();

        const q = this.input.value.trim();

        if (q.length > 0) {
          window.location.href =
            `${window.Shopify.routes.root}search?q=${encodeURIComponent(q)}&options[prefix]=last`;
        }
      }
    });
  }

  debounce(fn, delay) {
    let t;

    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  handleReset(e) {
    e.preventDefault();

    this.input.value = "";
    this.showEmpty(true);
  }

  handleInput() {
    this.input.value =
      this.input.value.replace(/[^a-zA-Z0-9 _-]/g, "");

    const q = this.input.value.trim();

    if (q.length === 0) {
      this.showEmpty(true);
      return;
    }

    fetch(
      `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,collection,article,page`
    )
      .then(res => res.json())
      .then(data => this.renderResults(data.resources.results))
      .catch(err =>
        console.error('Error fetching results:', err)
      );
  }

  showEmpty(state) {
    this.emptyWrapper.style.display =
      state ? "flex" : "none";

    this.resultWrapper.style.display =
      state ? "none" : "block";
  }

  async getLowestInStockPrice(product) {
    try {

      const cleanUrl =
        product.url.split("?")[0];

      const response =
        await fetch(`${cleanUrl}.js`);

      if (!response.ok) {
        throw new Error("Unable to load product");
      }

      const productData =
        await response.json();

      const inStockVariants =
        productData.variants.filter(variant => {

          /*
           * inventory_quantity is preferred.
           * available is used as fallback where Shopify
           * doesn't expose quantity.
           */

          if (
            typeof variant.inventory_quantity === "number"
          ) {
            return variant.inventory_quantity > 0;
          }

          return variant.available;
        });

      if (!inStockVariants.length) {
        return {
          price: product.price_min,
          compareAtPrice:
            product.compare_at_price_min
        };
      }

      const cheapest =
        inStockVariants.reduce((lowest, variant) => {

          if (
            !lowest ||
            variant.price < lowest.price
          ) {
            return variant;
          }

          return lowest;

        }, null);

      return {
        price: cheapest.price,
        compareAtPrice:
          cheapest.compare_at_price
      };

    } catch (error) {

      console.error(
        "Error getting in-stock price:",
        error
      );

      return {
        price: product.price_min,
        compareAtPrice:
          product.compare_at_price_min
      };
    }
  }

  async renderResults(results) {

    const {
      products = [],
      collections = [],
      articles = [],
      pages = [],
      queries = []
    } = results;

    this.showEmpty(false);

    this.resultInner.innerHTML = "";

    const addGroup = (title, html) => {

      const wrap =
        document.createElement('div');

      wrap.classList.add('search_group');

      wrap.innerHTML =
        `<h6>${title}</h6>${html}`;

      this.resultInner.appendChild(wrap);
    };

    if (queries.length) {

      addGroup(
        "Suggestions",

        queries.map(q => `
          <div class="query_card">
            <p>
              <a
                href="${q.url}"
                aria-label="${q.text}"
              >
                ${q.styled_text}
              </a>
            </p>
          </div>
        `).join("")
      );
    }

    if (collections.length) {

      addGroup(
        "Collections",

        collections.map(c => `
          <div class="search_result_collection_card">
            <p>
              <a
                href="${c.url}"
                aria-label="${c.title}"
              >
                ${c.title}
              </a>
            </p>
          </div>
        `).join("")
      );
    }

    if (products.length) {

      /*
       * Get the cheapest IN-STOCK variant
       * for every product returned by predictive search.
       */

      const productPrices =
        await Promise.all(
          products.map(product =>
            this.getLowestInStockPrice(product)
          )
        );

      addGroup(
        "Products",

        products.map((p, index) => {

          const pricing =
            productPrices[index];

          const price =
            pricing.price;

          const compareAtPrice =
            pricing.compareAtPrice;

          return `
            <div class="search_result_product_card">

              <div
                class="search_result_product_card_img ratio"
                style="--aspect-ratio: 90%;"
              >

                <a
                  href="${p.url}"
                  aria-label="${p.title}"
                >
                  <img
                    src="${p.featured_image?.url}"
                    alt="${p.title}"
                  >
                </a>

              </div>

              <div class="search_result_product_card_info">

                <p>
                  <a
                    href="${p.url}"
                    aria-label="${p.title}"
                  >
                    ${p.title}
                  </a>
                </p>

                <div class="search_result_product_price">

                  <span>
                    ${Shopify.formatMoney(price)}
                  </span>

                  ${
                    compareAtPrice &&
                    compareAtPrice > price

                    ? `<s>
                        ${Shopify.formatMoney(compareAtPrice)}
                       </s>`

                    : ""
                  }

                </div>

              </div>

            </div>
          `;

        }).join("")
      );
    }

    if (pages.length) {

      addGroup(
        "Pages",

        pages.map(pg => `
          <div class="search_result_page_card">
            <p>
              <a
                href="${pg.url}"
                aria-label="${pg.title}"
              >
                ${pg.title}
              </a>
            </p>
          </div>
        `).join("")
      );
    }

    if (articles.length) {

      addGroup(
        "Articles",

        articles.map(a => `
          <div class="search_result_article_card">

            <div
              class="search_result_article_card_img ratio"
              style="--aspect-ratio: 90%;"
            >

              <a
                href="${a.url}"
                aria-label="${a.title}"
              >
                <img
                  src="${a.image}"
                  alt="${a.title}"
                >
              </a>

            </div>

            <div class="search_result_article_info">

              <p>${a.title}</p>

              <a
                href="${a.url}"
                aria-label="${a.title}"
              >
                Read More
              </a>

            </div>

          </div>
        `).join("")
      );
    }

    if (
      !queries.length &&
      !collections.length &&
      !products.length &&
      !articles.length &&
      !pages.length
    ) {

      this.resultInner.innerHTML =
        `<h6 class="empty_results_heading">
          No results found matching your query.
        </h6>`;
    }
  }
}

customElements.define(
  'search-drawer',
  SearchDrawer
);